import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, MessageCircle } from 'lucide-react';
import { trackContactForm } from '@/utils/analytics';

const ContactUsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Send contact form data to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        // Track contact form lead conversion
        trackContactForm(formData.name, formData.email);
        
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        
        // Reset success message after 5 seconds
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        alert('Failed to send message. Please try again or email us directly at support@itrquick.com');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      alert('Failed to send message. Please email us directly at support@itrquick.com');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: ['support@itrquick.com'],
      link: 'mailto:support@itrquick.com'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+91 7899598868', 'Mon-Sat: 9 AM - 6 PM'],
      link: 'tel:+917899598868'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp Us',
      details: ['+91 7899598868', 'Quick Response'],
      link: 'https://wa.me/917899598868?text=Hi,%20I%20need%20help%20with%20my%20tax%20filing'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['6th Main, Horamavu Main Rd', 'Kallumantapa, Horamavu, Bengaluru, Karnataka 560043'],
      link: null
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Monday - Saturday', '9:00 AM - 6:00 PM IST'],
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-gradient-to-r from-purple-600 to-purple-700 text-white py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6" data-testid="contact-title">Contact Us</h1>
          <p className="text-xl text-purple-100 max-w-3xl">
            Have questions? We're here to help. Get in touch with our expert team for any queries about tax filing, GST, or business services.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 px-4" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 -mt-20">
            {contactInfo.map((info) => (
              <div key={info.title} className="bg-white rounded-xl p-6 shadow-lg" data-testid={`contact-${info.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-4">
                  <info.icon className="text-purple-600" size={24} />
                </div>
                <h3 className="text-lg font-semibold mb-3">{info.title}</h3>
                {info.link ? (
                  <a href={info.link} className="text-purple-600 hover:text-purple-700">
                    {info.details.map((detail) => (
                      <p key={`${info.title}-${detail}`} className="text-gray-600">{detail}</p>
                    ))}
                  </a>
                ) : (
                  info.details.map((detail) => (
                    <p key={`${info.title}-${detail}`} className="text-gray-600">{detail}</p>
                  ))
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 px-4" data-testid="form-map-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-xl p-8 shadow-sm" data-testid="contact-form">
              <h2 className="text-3xl font-bold mb-6">Send Us a Message</h2>
              
              {submitted && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="success-message">
                  <CheckCircle className="text-green-600" size={24} />
                  <p className="text-green-800">Thank you! Your message has been sent successfully. We'll get back to you soon.</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter your full name"
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="your.email@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="10-digit mobile number"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="What is this regarding?"
                    data-testid="contact-subject-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Tell us more about your query..."
                    data-testid="contact-message-input"
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold hover:bg-purple-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="contact-submit-button"
                >
                  {loading ? 'Sending...' : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
            
            {/* Map */}
            <div className="bg-white rounded-xl overflow-hidden shadow-sm" data-testid="map-section">
              <h2 className="text-3xl font-bold p-8 pb-6">Our Location</h2>
              <div className="relative" style={{ height: '500px' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.8346857573757!2d77.64558731482238!3d13.044861190810962!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDAyJzQxLjUiTiA3N8KwMzgnNTEuOCJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="ITRQUICK.COM Office Location"
                  data-testid="google-map"
                ></iframe>
              </div>
              <div className="p-8 pt-6">
                <p className="text-gray-600 mb-2"><strong>Address:</strong></p>
                <p className="text-gray-700 mb-4">
                  ITRQUICK.COM<br />
                  6th Main, Horamavu Main Rd<br />
                  opposite Style Union, Kallumantapa<br />
                  Horamavu, Bengaluru, Karnataka 560043
                </p>
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=13.044861,77.646457&query_place_id=ChIJITRQRQYVrjsR_3hZQQ0KYwY"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-700 font-medium inline-flex items-center gap-2"
                  data-testid="view-map-link"
                >
                  <MapPin size={18} />
                  View on Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-purple-600 pl-6">
              <h3 className="text-lg font-semibold mb-2">How do I view my uploaded documents?</h3>
              <p className="text-gray-600">Go to the <strong>Tax Locker</strong> tab to view all your uploaded documents.</p>
            </div>
            <div className="border-l-4 border-purple-600 pl-6">
              <h3 className="text-lg font-semibold mb-2">What should I do if I forgot to provide the password for my uploaded files?</h3>
              <p className="text-gray-600">Simply go to <strong>Tax Locker</strong> and next to each file you can see <strong>update password</strong> option.</p>
            </div>
            <div className="border-l-4 border-purple-600 pl-6">
              <h3 className="text-lg font-semibold mb-2">I am unable to upload my file. What should I do?</h3>
              <p className="text-gray-600">If your file exceeds 5 MB or you are facing upload issues, please contact us. Our team will get in touch with you and help you submit your documents.</p>
            </div>
            <div className="border-l-4 border-purple-600 pl-6">
              <h3 className="text-lg font-semibold mb-2">How quickly will I get a response?</h3>
              <p className="text-gray-600">We typically respond to all inquiries within 24 hours during business days.</p>
            </div>
            <div className="border-l-4 border-purple-600 pl-6">
              <h3 className="text-lg font-semibold mb-2">Can I visit your office?</h3>
              <p className="text-gray-600">Yes, you can visit us during business hours (Mon-Sat, 9 AM - 6 PM). We recommend scheduling an appointment in advance.</p>
            </div>
            <div className="border-l-4 border-purple-600 pl-6">
              <h3 className="text-lg font-semibold mb-2">Do you provide consultation over the phone?</h3>
              <p className="text-gray-600">Yes, our CA experts are available for phone consultations. Call us at +91 7899598868 during business hours.</p>
            </div>
            <div className="border-l-4 border-purple-600 pl-6">
              <h3 className="text-lg font-semibold mb-2">Can I upload more documents after submission?</h3>
              <p className="text-gray-600">Yes! After uploading all documents, you can view and upload more documents in the Tax Locker tab on your dashboard.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUsPage;